(() => {
    class AmaraTestimonials {
        constructor(section) {
            this.section = section;

            this.slides = Array.from(
                section.querySelectorAll('[data-amara-review-slide]')
            );

            this.modal =
                section.querySelector('[data-amara-review-modal]');

            this.modalBody =
                section.querySelector('[data-amara-review-modal-body]');

            this.lastTrigger = null;

            if (!this.slides.length) {
                return;
            }

            this.bindEvents();
        }

        bindEvents() {
            this.section
                .querySelectorAll('[data-amara-review-open]')
                .forEach((button) => {
                    button.addEventListener(
                        'click',
                        () => {
                            const slide =
                                button.closest(
                                    '[data-amara-review-slide]'
                                );

                            this.openModal(
                                slide,
                                button
                            );
                        }
                    );
                });

            this.section
                .querySelectorAll('[data-amara-review-close]')
                .forEach((button) => {
                    button.addEventListener(
                        'click',
                        () => this.closeModal()
                    );
                });

            this.handleKeydown =
                this.handleKeydown.bind(this);

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );
        }

        openModal(slide, trigger) {
            if (
                !slide ||
                !this.modal ||
                !this.modalBody
            ) {
                return;
            }

            const template =
                slide.querySelector(
                    '[data-amara-review-template]'
                );

            if (!template) {
                return;
            }

            this.lastTrigger = trigger;

            this.modalBody.innerHTML = '';

            this.modalBody.appendChild(
                template.content.cloneNode(true)
            );

            clearTimeout(this.closeTimer);

            this.modal.hidden = false;

            // Unhide first, then flip the class on the next frame so the
            // browser has a start state to transition from.
            requestAnimationFrame(
                () => this.modal.classList.add('is-open')
            );

            document.body.classList.add(
                'amara-review-modal-open'
            );

            this.initModalGallery();

            const closeButton =
                this.modal.querySelector(
                    '.amara-review-modal__close'
                );

            requestAnimationFrame(
                () => closeButton?.focus()
            );
        }

        closeModal() {
            if (
                !this.modal ||
                this.modal.hidden
            ) {
                return;
            }

            this.pauseModalVideos();

            this.modal.classList.remove('is-open');

            document.body.classList.remove(
                'amara-review-modal-open'
            );

            // The hidden attribute is display:none, which would kill the
            // transition, so it is applied only after the fade-out has run.
            // Duration is read from CSS so the two can never drift apart.
            const declared = getComputedStyle(this.modal)
                .getPropertyValue('--amara-modal-duration')
                .trim();

            const duration = declared.endsWith('ms')
                ? parseFloat(declared)
                : parseFloat(declared) * 1000 || 0;

            const finish = () => {
                this.modal.hidden = true;
                this.modalBody.innerHTML = '';
            };

            if (
                duration > 0 &&
                !window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ) {
                this.closeTimer = setTimeout(finish, duration);
            } else {
                finish();
            }

            this.lastTrigger?.focus();

            this.lastTrigger = null;
        }

        initModalGallery() {
            const media = Array.from(
                this.modalBody.querySelectorAll(
                    '[data-amara-modal-media]'
                )
            );

            const thumbs = Array.from(
                this.modalBody.querySelectorAll(
                    '[data-amara-modal-thumb]'
                )
            );

            const prev =
                this.modalBody.querySelector(
                    '[data-amara-modal-prev]'
                );

            const next =
                this.modalBody.querySelector(
                    '[data-amara-modal-next]'
                );

            if (!media.length) {
                return;
            }

            let index = 0;

            const show = (newIndex) => {
                if (!media.length) {
                    return;
                }

                index =
                    (
                        newIndex +
                        media.length
                    ) %
                    media.length;

                media.forEach(
                    (item, itemIndex) => {
                        const active =
                            itemIndex === index;

                        item.hidden = !active;

                        item.classList.toggle(
                            'is-active',
                            active
                        );

                        if (!active) {
                            item
                                .querySelectorAll('video')
                                .forEach((video) => {
                                    video.pause();
                                });
                        }
                    }
                );

                thumbs.forEach(
                    (thumb, thumbIndex) => {
                        thumb.classList.toggle(
                            'is-active',
                            thumbIndex === index
                        );
                    }
                );
            };

            prev?.addEventListener(
                'click',
                () => show(index - 1)
            );

            next?.addEventListener(
                'click',
                () => show(index + 1)
            );

            thumbs.forEach(
                (thumb, thumbIndex) => {
                    thumb.addEventListener(
                        'click',
                        () => show(thumbIndex)
                    );
                }
            );

            /*
             * Hide gallery arrows when the
             * testimonial only contains one
             * media item.
             */
            if (media.length <= 1) {
                prev?.setAttribute(
                    'hidden',
                    ''
                );

                next?.setAttribute(
                    'hidden',
                    ''
                );
            }

            show(0);
        }

        pauseModalVideos() {
            if (!this.modalBody) {
                return;
            }

            this.modalBody
                .querySelectorAll('video')
                .forEach((video) => {
                    video.pause();
                });
        }

        handleKeydown(event) {
            if (
                event.key === 'Escape' &&
                this.modal &&
                !this.modal.hidden
            ) {
                this.closeModal();
            }
        }

        destroy() {
            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );

            this.pauseModalVideos();

            document.body.classList.remove(
                'amara-review-modal-open'
            );
        }
    }


    const instances =
        new WeakMap();


    const init = (root = document) => {
        root
            .querySelectorAll(
                '[data-amara-testimonials]'
            )
            .forEach((section) => {
                if (
                    instances.has(section)
                ) {
                    return;
                }

                instances.set(
                    section,
                    new AmaraTestimonials(
                        section
                    )
                );
            });
    };


    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            () => init()
        );
    } else {
        init();
    }


    /*
     * Shopify Theme Editor support.
     */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            init(event.target);
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            const section =
                event.target.querySelector(
                    '[data-amara-testimonials]'
                );

            if (!section) {
                return;
            }

            const instance =
                instances.get(section);

            instance?.destroy();

            instances.delete(section);
        }
    );
})();
